/**
 * API service for Voltaire AI frontend.
 * Centralized API calls with authentication and error handling.
 */

import env from '../config/env';

const API_BASE = env.apiUrl;

interface ApiOptions extends RequestInit {
  skipAuth?: boolean;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public data?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiCall<T = unknown>(
  endpoint: string,
  options: ApiOptions = {}
): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options;
  const url = `${API_BASE}${endpoint}`;

  // Get token from localStorage
  const token = localStorage.getItem('voltaire_token');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers,
  };

  // Add auth header if we have a token and auth isn't skipped
  if (token && !skipAuth) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  if (env.enableDebug) {
    console.log(`API ${fetchOptions.method || 'GET'}: ${url}`);
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');

    if (!response.ok) {
      // Redirect to login on 401 (expired/invalid token)
      if (response.status === 401 && !skipAuth) {
        localStorage.removeItem('voltaire_token');
        localStorage.removeItem('voltaire_user');
        window.location.href = '/login';
        throw new ApiError(401, 'Session expired — please log in again');
      }

      const errorData = isJson ? await response.json() : await response.text();
      throw new ApiError(
        response.status,
        errorData.detail || errorData.message || 'API request failed',
        errorData
      );
    }

    if (isJson) {
      return await response.json();
    }
    return (await response.text()) as unknown as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network error
    if (env.enableDebug) {
      console.error('API Network Error:', error);
    }
    throw new ApiError(0, 'Network error - please check your connection');
  }
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string, options?: ApiOptions) =>
    apiCall<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, data?: unknown, options?: ApiOptions) =>
    apiCall<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(endpoint: string, data?: unknown, options?: ApiOptions) =>
    apiCall<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T>(endpoint: string, data?: unknown, options?: ApiOptions) =>
    apiCall<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T>(endpoint: string, options?: ApiOptions) =>
    apiCall<T>(endpoint, { ...options, method: 'DELETE' }),
};

export default api;
